import { useState, useEffect, useRef } from 'react';

export default function MaterialIssueNote({ onDataUpdate, initialData, title = "Material Issue Note", codePrefix = "MIN" }) {
  const [isInitialized, setIsInitialized] = useState(false);
  const dateTimeRef = useRef(null);
  const [form, setForm] = useState({
    min_no: '',
    date_time: '',
    department_issuing: 'STORES',
    person_issuing: '',

    customer_name: '',
    customer_address: '',
    contact_person: '',
    contact_no: '',
    email_id: '',
    ref_doc_no: '',

    receiver_type: [],
    issue_type: '',
    purpose: [],
    details: '',
  });

  // Initialize form and notify parent
  useEffect(() => {
    if (!isInitialized) {
      const defaultForm = {
        min_no: '',
        date_time: '',
        department_issuing: 'STORES',
        person_issuing: '',

        customer_name: '',
        customer_address: '',
        contact_person: '',
        contact_no: '',
        email_id: '',
        ref_doc_no: '',

        receiver_type: [],
        issue_type: '',
        purpose: [],
        details: '',
      };
      
      let initialForm = defaultForm;
      
      if (initialData) {
        initialForm = {
          ...defaultForm,
          ...initialData,
          receiver_type: Array.isArray(initialData.receiver_type) ? initialData.receiver_type : defaultForm.receiver_type,
          purpose: Array.isArray(initialData.purpose) ? initialData.purpose : defaultForm.purpose,
        };
      }
      
      setForm(initialForm);
      setIsInitialized(true);
      
      if (onDataUpdate) {
        onDataUpdate(initialForm);
      }
    }
  }, [initialData, isInitialized, onDataUpdate]);

  useEffect(() => {
    if (onDataUpdate && isInitialized) {
      onDataUpdate(form);
    }
  }, [form, onDataUpdate, isInitialized]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const handleCheckbox = (name, value) => {
    setForm((p) => {
      const currentList = Array.isArray(p[name]) ? p[name] : [];

      if (name === 'receiver_type' || name === 'purpose') {
        const list = currentList.includes(value) ? [] : [value];
        return { ...p, [name]: list };
      }
      const list = currentList.includes(value)
        ? currentList.filter((v) => v !== value)
        : [...currentList, value];
      return { ...p, [name]: list };
    });
  };

  // Auto-generate code No if not provided
  const handleAutoGenerateCode = () => {
    if (!form.min_no) {
      const timestamp = new Date().getTime();
      const random = Math.floor(Math.random() * 1000);
      const codeNo = `${codePrefix}-${timestamp}-${random}`;
      setForm((p) => ({ ...p, min_no: codeNo }));
    }
  };

  return (
    <div style={styles.pageContainer}>
      {/* Header Section */}
      <div style={styles.headerSection}>
        <h1 style={styles.pageTitle}>{title}</h1>
        
        <div style={styles.headerStatus}>
          <div style={styles.statusInfo}>
            <div style={styles.statusDot}></div>
            Draft Mode
          </div>
          <button 
            style={styles.autoGenButton}
            onClick={handleAutoGenerateCode}
            title={`Auto-generate ${codePrefix} No`}
          >
            Auto-generate {codePrefix}
          </button>
        </div>
      </div>

      {/* Main Card Container */}
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <h3 style={styles.cardTitle}>
            <span>Basic Information</span>
          </h3>
          <div style={styles.cardHint}>Required fields marked with *</div>
        </div>
        
        <div style={styles.formGrid}>
          <div style={styles.formGroup}>
            <label style={styles.labelStyle}>
              {codePrefix} No <span style={styles.required}>*</span>
            </label>
            <div style={styles.inputWithAction}>
              <input
                name="min_no"
                value={form.min_no}
                onChange={handleChange}
                style={styles.inputStyle}
                placeholder="Auto-generated"
              />
              {!form.min_no && (
                <div style={styles.autoGenHint}>Click button above to auto-generate</div>
              )}
            </div>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.labelStyle}>
              Date / Time <span style={styles.required}>*</span>
            </label>
            <div style={{ position: 'relative' }}>
              <div
                onClick={() => dateTimeRef.current?.showPicker()}
                style={{
                  ...styles.inputStyle,
                  color: form.date_time ? '#1e40af' : '#9ca3af',
                  fontWeight: form.date_time ? '600' : 'normal',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                {form.date_time ? (() => {
                  const d = new Date(form.date_time.replace(' ', 'T'));
                  return d.toLocaleDateString('en-GB') + ' ' + d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
                })() : 'Select date & time'}
              </div>
              <input
                ref={dateTimeRef}
                type="datetime-local"
                name="date_time"
                value={form.date_time}
                onChange={handleChange}
                style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', width: 0, height: 0 }}
              />
            </div>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.labelStyle}>
              Department Issuing
            </label>
            <input
              name="department_issuing"
              value={form.department_issuing}
              onChange={handleChange}
              style={styles.inputStyle}
              placeholder="e.g., STORES"
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.labelStyle}>
              Person Issuing <span style={styles.required}>*</span>
            </label>
            <input
              name="person_issuing"
              value={form.person_issuing}
              onChange={handleChange}
              style={styles.inputStyle}
              placeholder="Enter name"
              required
            />
          </div>
        </div>
      </div>

      {/* Customer Details Card */}
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <h3 style={styles.cardTitle}>
            <span>Customer Details</span>
            <div style={styles.subtitle}>(If issued directly to customer)</div>
          </h3>
        </div>
        
        <div style={styles.formGrid}>
          <div style={styles.formGroup}>
            <label style={styles.labelStyle}>Name</label>
            <input
              name="customer_name"
              value={form.customer_name}
              onChange={handleChange}
              style={styles.inputStyle}
              placeholder="Customer/Company name"
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.labelStyle}>Contact Person</label>
            <input
              name="contact_person"
              value={form.contact_person}
              onChange={handleChange}
              style={styles.inputStyle}
              placeholder="Contact person name"
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.labelStyle}>Contact No</label>
            <input
              name="contact_no"
              value={form.contact_no}
              onChange={handleChange}
              style={styles.inputStyle}
              placeholder="Phone number"
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.labelStyle}>Email ID</label>
            <input
              name="email_id"
              value={form.email_id}
              onChange={handleChange}
              style={styles.inputStyle}
              type="email"
              placeholder="email@example.com"
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.labelStyle}>Ref Doc No</label>
            <input
              name="ref_doc_no"
              value={form.ref_doc_no}
              onChange={handleChange}
              style={styles.inputStyle}
              placeholder="Reference document number"
            />
          </div>

          <div style={styles.formGroupFull}>
            <label style={styles.labelStyle}>Address</label>
            <textarea
              name="customer_address"
              value={form.customer_address}
              onChange={handleChange}
              style={styles.textareaStyle}
              placeholder="Full address"
              rows="2"
            />
          </div>
        </div>
      </div>

      {/* Receiver Details Card */}
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <h3 style={styles.cardTitle}>
            <span>Receiver Details</span>
            <div style={styles.subtitle}>(If issued internally)</div>
          </h3>
        </div>
        
        <div style={styles.checkboxGrid}>
          {['Customer', 'Manufacturing', 'Service', 'Design', 'QC', 'Testing', 'Supplier', 'Other'].map((item) => (
            <label key={item} style={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={form.receiver_type.includes(item)}
                onChange={() => handleCheckbox('receiver_type', item)}
                style={styles.checkboxInput}
              />
              <span style={styles.checkboxText}>{item}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Issue Type Card */}
      <div style={styles.card}>
        <h3 style={styles.cardTitle}>Issue Type</h3>
        
        <div style={styles.radioGroup}>
          <label style={styles.radioLabel}>
            <input
              type="radio"
              name="issue_type"
              value="Non Returnable"
              checked={form.issue_type === 'Non Returnable'}
              onChange={handleChange}
              style={styles.radioInput}
            />
            <div style={styles.radioBox}>
              <div style={styles.radioTitle}>Non Returnable</div>
              <div style={styles.radioDescription}>Items will not be returned</div>
            </div>
          </label>
          
          <label style={styles.radioLabel}>
            <input
              type="radio"
              name="issue_type"
              value="Returnable"
              checked={form.issue_type === 'Returnable'}
              onChange={handleChange}
              style={styles.radioInput}
            />
            <div style={styles.radioBox}>
              <div style={styles.radioTitle}>Returnable</div>
              <div style={styles.radioDescription}>Items must be returned</div>
            </div>
          </label>
        </div>
      </div>

      {/* Purpose Card */}
      <div style={styles.card}>
        <h3 style={styles.cardTitle}>Purpose</h3>
        
        <div style={styles.purposeGrid}>
          {[
            'Warranty Replacement',
            'Service Replacement',
            'Customer Approval / Demo',
            'Repairs / Modification',
            'Sample',
            'Internal Use',
            'For Equipment Manufacturing',
            'Free Issue',
          ].map((item) => (
            <label key={item} style={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={form.purpose.includes(item)}
                onChange={() => handleCheckbox('purpose', item)}
                style={styles.checkboxInput}
              />
              <span style={styles.checkboxText}>{item}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Details Card */}
      <div style={styles.card}>
        <h3 style={styles.cardTitle}>Details / Remarks</h3>
        
        <div style={styles.formGroup}>
          <textarea
            rows={3}
            name="details"
            value={form.details}
            onChange={handleChange}
            style={styles.textareaStyle}
            placeholder="Enter additional details, remarks, or specific instructions..."
          />
        </div>
      </div>
    </div>
  );
}

/* =========================
   UNIFIED STYLES
   ========================= */
const styles = {
  autoGenButton: {
    padding: '6px 12px',
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s',
    marginTop: '8px',
    ':hover': {
      backgroundColor: '#2563eb',
    },
  },
  
  inputWithAction: {
    position: 'relative',
  },
  
  autoGenHint: {
    fontSize: '11px',
    color: '#6b7280',
    marginTop: '4px',
    fontStyle: 'italic',
  },

  pageContainer: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '16px',
    width: '100%',
    overflowX: 'auto',
  },
  
  headerSection: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '20px',
    width: '100%',
    gap: '20px',
    flexWrap: 'wrap',
  },
  
  pageTitle: {
    margin: 0,
    fontSize: '24px',
    color: '#1f2937',
    fontWeight: '600',
    flex: 1,
  },
  
  headerStatus: {
    backgroundColor: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    padding: '12px 16px',
    minWidth: '200px',
  },
  
  statusInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    color: '#475569',
    fontWeight: '500',
  },
  
  statusDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: '#f59e0b',
    animation: 'pulse 2s infinite',
  },
  
  card: {
    background: 'white',
    borderRadius: '8px',
    padding: '20px',
    marginBottom: '16px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    border: '1px solid #e5e7eb',
    transition: 'box-shadow 0.2s ease',
  },
  
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '16px',
    flexWrap: 'wrap',
    gap: '8px',
  },
  
  cardTitle: {
    margin: 0,
    fontSize: '16px',
    fontWeight: '600',
    color: '#1f2937',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
  },
  
  cardHint: {
    fontSize: '13px',
    color: '#6b7280',
    fontWeight: '400',
  },
  
  subtitle: {
    fontSize: '13px',
    color: '#6b7280',
    fontWeight: '400',
    fontStyle: 'normal',
  },
  
  formGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '16px',
  },
  
  formGroup: {
    marginBottom: '0',
  },
  
  formGroupFull: {
    gridColumn: '1 / -1',
    marginBottom: '0',
  },
  
  labelStyle: {
    display: 'block',
    marginBottom: '6px',
    fontSize: '13px',
    fontWeight: '500',
    color: '#374151',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  
  required: {
    color: '#dc2626',
    fontSize: '14px',
  },
  
  inputStyle: {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontSize: '14px',
    boxSizing: 'border-box',
    transition: 'all 0.2s',
    backgroundColor: '#f9fafb',
    height: '40px',
  },
  
  textareaStyle: {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontSize: '14px',
    fontFamily: 'inherit',
    resize: 'vertical',
    boxSizing: 'border-box',
    transition: 'all 0.2s',
    minHeight: '80px',
    lineHeight: '1.5',
    backgroundColor: '#f9fafb',
  },
  
  fieldHint: {
    fontSize: '11px',
    color: '#9ca3af',
    marginTop: '4px',
    fontStyle: 'italic',
  },
  
  checkboxGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '12px',
  },
  
  purposeGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '12px',
  },
  
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    borderRadius: '6px',
    border: '1px solid #e5e7eb',
    backgroundColor: '#f9fafb',
    cursor: 'pointer',
    transition: 'all 0.2s',
    fontSize: '14px',
  },
  
  checkboxInput: {
    width: '16px',
    height: '16px',
    accentColor: '#3b82f6',
    cursor: 'pointer',
  },
  
  checkboxText: {
    fontSize: '14px',
    color: '#374151',
    fontWeight: '400',
  },
  
  radioGroup: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '16px',
  },
  
  radioLabel: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    padding: '12px 16px',
    borderRadius: '8px',
    border: '2px solid #e5e7eb',
    backgroundColor: '#f9fafb',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  
  radioInput: {
    marginTop: '2px',
    width: '18px',
    height: '18px',
    accentColor: '#3b82f6',
    cursor: 'pointer',
  },
  
  radioBox: {
    flex: 1,
  },
  
  radioTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#374151',
    marginBottom: '4px',
  },
  
  radioDescription: {
    fontSize: '13px',
    color: '#6b7280',
    lineHeight: '1.4',
  },
  
  actionButtons: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    marginTop: '20px',
    paddingTop: '20px',
    borderTop: '1px solid #e5e7eb',
  },
  
  primaryButton: {
    padding: '10px 24px',
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s',
    minWidth: '180px',
    height: '40px',
  },
  
  secondaryButton: {
    padding: '10px 24px',
    backgroundColor: 'transparent',
    color: '#6b7280',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s',
    minWidth: '120px',
    height: '40px',
  },
  
  /* Hover States */
  inputStyle: {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontSize: '14px',
    boxSizing: 'border-box',
    transition: 'all 0.2s',
    backgroundColor: '#f9fafb',
    height: '40px',
    ':hover': {
      borderColor: '#93c5fd',
    },
    ':focus': {
      outline: 'none',
      borderColor: '#3b82f6',
      boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
      backgroundColor: 'white',
    },
  },
  
  textareaStyle: {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontSize: '14px',
    fontFamily: 'inherit',
    resize: 'vertical',
    boxSizing: 'border-box',
    transition: 'all 0.2s',
    minHeight: '80px',
    lineHeight: '1.5',
    backgroundColor: '#f9fafb',
    ':hover': {
      borderColor: '#93c5fd',
    },
    ':focus': {
      outline: 'none',
      borderColor: '#3b82f6',
      boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
      backgroundColor: 'white',
    },
  },
  
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    borderRadius: '6px',
    border: '1px solid #e5e7eb',
    backgroundColor: '#f9fafb',
    cursor: 'pointer',
    transition: 'all 0.2s',
    fontSize: '14px',
    ':hover': {
      borderColor: '#93c5fd',
      backgroundColor: '#eff6ff',
    },
  },
  
  radioLabel: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    padding: '12px 16px',
    borderRadius: '8px',
    border: '2px solid #e5e7eb',
    backgroundColor: '#f9fafb',
    cursor: 'pointer',
    transition: 'all 0.2s',
    ':hover': {
      borderColor: '#93c5fd',
      backgroundColor: '#eff6ff',
    },
  },
  
  primaryButton: {
    padding: '10px 24px',
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s',
    minWidth: '180px',
    height: '40px',
    ':hover': {
      backgroundColor: '#2563eb',
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 6px rgba(37, 99, 235, 0.2)',
    },
  },
  
  secondaryButton: {
    padding: '10px 24px',
    backgroundColor: 'transparent',
    color: '#6b7280',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s',
    minWidth: '120px',
    height: '40px',
    ':hover': {
      backgroundColor: '#f3f4f6',
      color: '#374151',
    },
  },
  
  '@keyframes pulse': {
    '0%, 100%': {
      opacity: 1,
    },
    '50%': {
      opacity: 0.5,
    },
  },
};